import { useCallback, useEffect, useRef } from 'react';
import { PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import queryString from 'query-string';
import { updateUrl } from '@/utils';
import { StateMap } from '@/utils/stateMap';
import { LabellingCuboid } from '@/types';
import { StyledCuboid } from '@/modules/cuboids/types';
import { useCuboidState } from '../hooks/useCuboidState';
import { useCuboidStyleHandlers } from '../hooks/useCuboidStyleHandlers';
import { useSelectCuboid } from '@/modules/cuboids/hooks/useSelectCuboid';
import * as globalState from '@/state';
import { TransformMode } from '@fabric-web/3d-viewer';

export type CurrentCuboidDeeplinkState = {
  shouldUpdateUrl: boolean;
  currentCuboidAtom: PrimitiveAtom<StyledCuboid<LabellingCuboid> | undefined>;
  cuboids: StateMap<string, LabellingCuboid>;
  isCuboidVisibilityBoxEnabledAtom: PrimitiveAtom<boolean>;
};

export const useCurrentCuboidDeeplink = (state: CurrentCuboidDeeplinkState) => {
  const { shouldUpdateUrl, currentCuboidAtom, cuboids, isCuboidVisibilityBoxEnabledAtom } = state;

  const currentCuboid = useAtomValue(currentCuboidAtom);
  const isCuboidVisibilityBoxEnabled = useAtomValue(isCuboidVisibilityBoxEnabledAtom);

  const setIsCuboidVisibilityBoxEnabled = useSetAtom(isCuboidVisibilityBoxEnabledAtom);
  const setCuboidTransformMode = useSetAtom(globalState.cuboidTransformMode);

  const cuboidState = useCuboidState();
  const styleHandlers = useCuboidStyleHandlers();
  const selectCuboid = useSelectCuboid(cuboidState, styleHandlers);

  const pendingCurrentCuboidId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('hidePointsOutside', isCuboidVisibilityBoxEnabled ? '1' : '0');
    }
  }, [shouldUpdateUrl, isCuboidVisibilityBoxEnabled]);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('currentCuboid', currentCuboid?.cuboidData.id);
    }
  }, [shouldUpdateUrl, currentCuboid]);

  useEffect(() => {
    const pendingId = pendingCurrentCuboidId.current;
    if (!pendingId) return;
    const cuboid = cuboids.get(pendingId);
    if (!cuboid) return;
    selectCuboid(cuboid);
    setCuboidTransformMode(TransformMode.View);
    pendingCurrentCuboidId.current = undefined;
  }, [cuboids, selectCuboid, setCuboidTransformMode]);

  const applyFromParsed = useCallback(
    (parsed: queryString.ParsedQuery<string>) => {
      if (parsed.hidePointsOutside === '1') {
        setIsCuboidVisibilityBoxEnabled(true);
      } else if (parsed.hidePointsOutside === '0') {
        setIsCuboidVisibilityBoxEnabled(false);
      }

      if (parsed.currentCuboid && typeof parsed.currentCuboid === 'string') {
        pendingCurrentCuboidId.current = parsed.currentCuboid;
      }
    },
    [setIsCuboidVisibilityBoxEnabled]
  );

  return { applyFromParsed };
};
